/**
 * Platform-scoped roles API.
 *
 * Distinct from `api/roles.ts` (which is company-scoped, calling
 * /api/companies/:companyId/roles). This module targets the
 * /api/admin/roles/* surface the admin plugin exposes.
 */

import type { Role } from '../types/access';
import { apiFetch } from './client';

const BASE_URL = '/api/admin/roles';

export interface PlatformRoleFilters {
  includeSystem?: boolean;
}

/**
 * Payload for POST /api/admin/roles. Mirrors the backend
 * `CreatePlatformRoleDto` exactly. Platform roles do NOT take a `code`
 * field (the backend doesn't store one); the company-scoped role flow is
 * the only place that uses `code`.
 */
export interface CreatePlatformRoleInput {
  name: string;
  description?: string;
  accessIds: string[];
  scope?: 'SYSTEM' | 'COMPANY' | 'DEPARTMENT' | 'PROJECT';
  level?: number;
  canDelegate?: boolean;
  maxUsers?: number;
  expiryDate?: string | Date;
  approvalRequired?: boolean;
}

export const listPlatformRoles = async (
  filters?: PlatformRoleFilters
): Promise<Role[]> => {
  const params = new URLSearchParams();
  if (filters?.includeSystem === false) params.set('includeSystem', 'false');
  const qs = params.toString();
  return apiFetch<Role[]>(`${BASE_URL}${qs ? `?${qs}` : ''}`);
};

export const getPlatformRole = async (id: string): Promise<Role> =>
  apiFetch<Role>(`${BASE_URL}/${id}`);

export const createPlatformRole = async (
  dto: CreatePlatformRoleInput
): Promise<Role> =>
  apiFetch<Role>(BASE_URL, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
